import styles from './spacedrawer.module.scss';
import Drawer from '@mui/material/Drawer';
import { useEffect, useMemo, useState } from 'react';
import CloseIcon from '@mui/icons-material/Close';
import IconButton from '@mui/material/IconButton';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import { Button, TextField } from '@mui/material';
import NumberFormatCustom from 'components/common/NumberFormatCustom';
import { allElementsEquals, sum } from 'utils/arrays';
import ConfirmModal from 'components/common/ConfirmModal/ConfirmModal';
import { useDispatch } from 'react-redux';
import actions from 'store/actions';
import { useValidator } from 'hooks/useValidator';
import { updateNA, updateExternalAccessNA } from 'store/na/thunk';
import { useTranslation } from 'react-i18next';
import { getUnits } from 'utils/units';
import { round } from 'lodash';
import { addThousandSeparator } from 'utils/numbers';
import { getFormatSeparatorsOptions, decimalPrecision } from 'config/marketConfig';
import { weakKey } from 'utils/objects';

const DRAWER_ANCHOR_TYPE = 'right';

export default function SpaceDrawer(props) {
  const { disabled, isExternalAccess } = props;
  const { quantityMinimum, quantityMaximum } = props.quantityBounds;
  const dispatch = useDispatch();
  const state = useMemo(() => props.state, [props]);
  const spaces = useMemo(() => props.spaceType?.spaces || [], [props]);
  const { t } = useTranslation();

  const [openModal, setOpenModal] = useState(false);
  const [name, setName] = useState(props.spaceType?.name);
  const [quantities, setQuantities] = useState(spaces.map((space) => space.quantity));

  const [validables, validator] = useValidator(['name'], () => {
    const message = {};
    if (!name) message.name = t('step5.spaceDrawer.error1');
    return message;
  });

  useEffect(() => {
    setQuantities(spaces.map((space) => space.quantity));
    setName(props.spaceType?.name);
  }, [spaces, props]);

  const handleChangeQuantity = (index, value) => {
    const newQuantities = [...quantities];
    newQuantities[index] = Number(value);
    setQuantities(newQuantities);
  };

  const handleCancel = () => {
    const dirty =
      !allElementsEquals(
        quantities,
        spaces.map((space) => space.quantity),
      ) || name !== props.spaceType?.name;

    if (dirty) setOpenModal(true);
    else props.toggleDrawer(false);
  };

  const handleSaveChanges = () => {
    if (!validator.validate()) return;

    const spaceType = {
      ...props.spaceType,
      name: name,
      spaces: props.spaceType.spaces.map((space, index) => ({
        ...space,
        quantity: quantities[index],
      })),
    };
    dispatch(actions.na.updateSpaceType(spaceType));
    dispatch(isExternalAccess ? updateExternalAccessNA({ hasChanged: true }) : updateNA);

    setOpenModal(false);
    props.toggleDrawer(false);
  };

  const handleModalAction = (confirmIgnore) => {
    setOpenModal(false);
    if (confirmIgnore) {
      validator.reset();
      props.toggleDrawer(false);
    }
  };
  // TO-DO DRY step5
  const totalSpace = `${addThousandSeparator(
    sum(spaces, (space, index) => quantities[index] * space.size.width * space.size.height),
    { fixed: decimalPrecision, ...getFormatSeparatorsOptions() },
  )} ${t('step5.spaceDrawer.total')} ${getUnits().area}`;

  const FormattedSpaceSize = ({ width, height }) => {
    const { thousandSeparator, decimalSeparator } = getFormatSeparatorsOptions();
    const units = getUnits().unit;

    const formattedWidth = addThousandSeparator(round(width, decimalPrecision), {
      fixed: decimalPrecision,
      thousandSeparator,
      decimalSeparator,
    });

    const formattedHeight = addThousandSeparator(round(height, decimalPrecision), {
      fixed: decimalPrecision,
      thousandSeparator,
      decimalSeparator,
    });

    return (
      <div className={styles.size}>
        {formattedWidth}
        {units} x {formattedHeight}
        {units}
      </div>
    );
  };

  const getTotalSize = (space) => {
    const totalSize = space.size.width * space.size.height;
    const { thousandSeparator, decimalSeparator } = getFormatSeparatorsOptions();

    return addThousandSeparator(totalSize, {
      fixed: decimalPrecision,
      thousandSeparator,
      decimalSeparator,
    });
  };

  return (
    <Drawer
      data-testid="SpaceDrawer"
      anchor={DRAWER_ANCHOR_TYPE}
      open={state}
      onClose={() => props.toggleDrawer(false)}>
      <div className={styles.content}>
        <div className={styles.main}>
          <div className={styles.titles}>
            <div className={styles.first_title}>{t('step5.heading')}</div>
            <div className={styles.second_title}>{t('step5.spaceDrawer.editTitle')}</div>
          </div>
          <IconButton onClick={handleCancel} className={styles.close}>
            <CloseIcon />
          </IconButton>
        </div>
        <div className={styles.description}>{t('step5.spaceDrawer.editDescription')}</div>
        <div className={styles.spaces}>
          {spaces.map((space, index) => (
            <div key={weakKey(space)} className={styles.space}>
              <div className={styles.space_row1}>
                <img className={styles.image} src={space.imageUrl} alt={t(space.name)} />
                <div className={styles.name_size}>
                  <div className={styles.name}>{t(space.name)}</div>
                  <FormattedSpaceSize width={space.size.width} height={space.size.height} />
                </div>
                <div className={styles.total_size}>
                  {getTotalSize(space) + ` ${getUnits().area}`}
                </div>
              </div>
              <div className={styles.space_row2}>
                <div className={styles.controls}>
                  <IconButton
                    disabled={Number(quantities[index]) <= quantityMinimum || disabled}
                    onClick={() => handleChangeQuantity(index, Number(quantities[index]) - 1)}
                    className={styles.control_button}>
                    <RemoveIcon />
                  </IconButton>
                  <TextField
                    disabled={disabled}
                    className={styles.input_space}
                    value={quantities[index]}
                    onChange={(event) => handleChangeQuantity(index, event.target.value)}
                    inputProps={{
                      style: {
                        textAlign: 'center',
                        height: '3px',
                      },
                      min: quantityMinimum,
                      max: quantityMaximum,
                    }}
                    InputProps={{
                      inputComponent: NumberFormatCustom,
                    }}
                  />
                  <IconButton
                    disabled={Number(quantities[index]) >= quantityMaximum || disabled}
                    onClick={() => handleChangeQuantity(index, Number(quantities[index]) + 1)}
                    className={styles.control_button}>
                    <AddIcon />
                  </IconButton>
                </div>
              </div>
            </div>
          ))}
        </div>
        <div className={styles.summary}>
          <div className={styles.rename_space}>
            <div className={styles.rename_input_title}>{t('step5.spaceDrawer.rename')}</div>
            <TextField
              disabled={disabled}
              error={!validables.name.valid}
              helperText={validables.name.message}
              value={t(name)}
              onChange={(event) => setName(event.target.value)}
              inputProps={{
                className: styles.rename_input_box,
              }}
            />
          </div>
          <div className={styles.total_space}>
            <div>
              <div className={styles.total_input_title}>{t('step5.spaceDrawer.totalSpace')}</div>
              <TextField
                disabled
                className={styles.total_input_box}
                value={totalSpace}
                inputProps={{ className: styles.total_input_box }}
              />
              <div className={styles.buttons}>
                <Button
                  data-testid="SpaceDrawerCancleBtn"
                  className={styles.cancel_button}
                  size="small"
                  variant="contained"
                  onClick={handleCancel}>
                  {t('common.cancel')}
                </Button>
                <Button
                  disabled={disabled}
                  data-testid="SpaceDrawerSaveBtn"
                  className={styles.save_button}
                  size="small"
                  variant="contained"
                  onClick={handleSaveChanges}>
                  {t('step5.spaceDrawer.save')}
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
      <ConfirmModal
        title={t('step5.spaceDrawer.confirmationalTitle')}
        description={t('step5.spaceDrawer.confirmationalDescription')}
        open={openModal}
        onAction={handleModalAction}
      />
    </Drawer>
  );
}
